import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Minus } from "lucide-react";
import { useCart } from "@/cart/CartContext";

export default function ItemCard({ item, extras, allItems }) {
  const { add } = useCart();
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(item.sizes ? Object.keys(item.sizes)[0] : null);
  const [qty, setQty] = useState(1);
  const [picked, setPicked] = useState([]);
  const [choicePicks, setChoicePicks] = useState([]);

  const base = item.sizes ? item.sizes[size] : item.price;
  const chosen = extras.filter((e) => picked.includes(e.id));
  const extrasSum = chosen.reduce((s, e) => s + (e.sizes ? e.sizes[size] : e.price), 0);
  const lineUnit = base + extrasSum;

  const choiceSpec = item.choice;
  const choiceOptions = choiceSpec
    ? choiceSpec.options.map((id) => allItems.find((i) => i.id === id)).filter(Boolean)
    : [];
  const choicesReady = !choiceSpec || choicePicks.length === choiceSpec.count;

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleChoice = (id) =>
    setChoicePicks((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : choiceSpec.count === 1 ? [id] : p.length < choiceSpec.count ? [...p, id] : p
    );

  const addToCart = () => {
    if (!choicesReady) return;
    add({
      item_id: item.id,
      name: item.name,
      size,
      unit: lineUnit,
      extras: chosen.map((e) => ({ id: e.id, name: e.name, price: e.sizes ? e.sizes[size] : e.price })),
      choices: choicePicks.map((id) => ({ id, name: allItems.find((i) => i.id === id)?.name || id })),
      qty,
    });
    toast.success(`Added ${item.name}`, { description: size ? `${size} · $${(lineUnit * qty).toFixed(2)}` : `$${(lineUnit * qty).toFixed(2)}` });
    setOpen(false);
    setPicked([]);
    setChoicePicks([]);
    setQty(1);
  };

  return (
    <div className={`border-2 transition-colors duration-300 ${open ? "border-ember bg-white" : "border-zinc-700 bg-white hover:border-ember"}`} data-testid={`item-card-${item.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left px-5 py-4" data-testid={`item-open-${item.id}`}>
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-body font-bold text-xl text-ink">{item.name}</h3>
          <span className="font-mono font-bold text-lg text-ember whitespace-nowrap">
            {item.sizes ? `from $${Object.values(item.sizes)[0]}` : `$${item.price}`}
          </span>
        </div>
        {item.note && <p className="mt-1 text-xs text-ember">{item.note}</p>}
        <p className="mt-1 font-mono text-sm tracking-[0.15em] uppercase text-zinc-500">
          {open ? "Close" : "Customise & add"}
        </p>
      </button>

      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="px-5 pb-5 overflow-hidden">
          {item.sizes && (
            <div className="mb-4">
              <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(item.sizes).map(([s, p]) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    data-testid={`size-${item.id}-${s.replace(/\s/g, "-")}`}
                    className={`font-mono font-bold text-sm px-4 py-3 border transition-colors duration-300 ${size === s ? "border-ember bg-ember text-white" : "border-zinc-800 text-zinc-500 hover:border-ember"}`}
                  >
                    {s} · ${p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {extras.length > 0 && (
            <div className="mb-4">
              <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Extras</p>
              <div className="flex flex-wrap gap-2">
                {extras.map((ex) => {
                  const p = ex.sizes ? ex.sizes[size] : ex.price;
                  const on = picked.includes(ex.id);
                  return (
                    <button
                      key={ex.id}
                      type="button"
                      onClick={() => toggle(ex.id)}
                      data-testid={`extra-${item.id}-${ex.id}`}
                      className={`font-mono font-bold text-sm px-4 py-3 border transition-colors duration-300 ${on ? "border-ember bg-ember text-white" : "border-zinc-800 text-zinc-500 hover:border-ember hover:text-ember"}`}
                    >
                      {ex.name} +${p}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {choiceSpec && (
            <div className="mb-4">
              <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">
                Choose {choiceSpec.count} {choiceSpec.label} ({choicePicks.length}/{choiceSpec.count})
              </p>
              <div className="flex flex-wrap gap-2">
                {choiceOptions.map((o) => {
                  const on = choicePicks.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggleChoice(o.id)}
                      data-testid={`choice-${item.id}-${o.id}`}
                      className={`font-mono font-bold text-sm px-4 py-3 border transition-colors duration-300 ${on ? "border-ember bg-ember text-white" : "border-zinc-800 text-zinc-500 hover:border-ember hover:text-ember"}`}
                    >
                      {o.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {picked.includes("ex-pizza-topping") && (
            <p className="mb-4 font-mono text-xs tracking-[0.1em] uppercase text-ember border border-ember/50 bg-ember/10 px-3 py-2" data-testid={`toppings-note-${item.id}`}>
              List up to 2 extra toppings in the order notes at checkout
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center border-2 border-zinc-700">
              <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-zinc-500 hover:text-ember" aria-label="Less" data-testid={`qty-minus-${item.id}`}>
                <Minus className="w-5 h-5" />
              </button>
              <span className="px-4 font-mono font-bold text-base" data-testid={`qty-value-${item.id}`}>{qty}</span>
              <button type="button" onClick={() => setQty(Math.min(20, qty + 1))} className="px-4 py-3 text-zinc-500 hover:text-ember" aria-label="More" data-testid={`qty-plus-${item.id}`}>
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={addToCart}
              disabled={!choicesReady}
              data-testid={`add-${item.id}`}
              className="inline-flex items-center gap-2 bg-ember hover:bg-ember-hover disabled:opacity-50 text-white font-bold px-6 py-3.5 text-base tracking-wide uppercase transition-colors duration-300"
            >
              <Plus className="w-5 h-5" /> Add — ${(lineUnit * qty).toFixed(2)}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
